import React, { useContext, useEffect, useRef, useState } from 'react'
import {
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Chip,
    Tooltip
} from '@mui/material'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import CloseIcon from '@mui/icons-material/Close'
import { makeStyles } from '@mui/styles'
import { styled } from '@mui/material/styles'
import moment from 'moment'
import { useNavigate } from 'react-router-dom'

import Controls from '../Entry/Controls'
import Popup from '../../layouts/Popup'
import Drawer from '../../layouts/Drawer'
import ConfirmDialog from '../../layouts/ConfirmDialog'
import Notification from '../../layouts/Notification'
import Toolbar from './Toolbar'
import Loader from '../Entry/Loading'

import { GlobalContext } from '../../applications/hooks/UseContext'
import { useAuth } from '../../applications/hooks/UseAuth'
import { FaTrashRestore } from 'react-icons/fa'
import iconExcel from '../../assets/image/iconExcel.png'
import * as XLSX from 'xlsx'
import { saveAs } from 'file-saver'
import { isNumber, numStr } from '../../applications/UtilFunctions'

const useStyles = makeStyles(() => ({
    pageContent: { marginTop: 20, backgroundColor: '#F2F8F6' },
    cell: {
        textAlign: 'center'
    },
    table: {
        '& thead th': {
            fontWeight: '600',
            padding: '15px 5px',
            backgroundColor: '#FFFFFF',
            textAlign: 'left'
        },
        '& tbody td': {
            fontWeight: '200',
            padding: '15px 5px',
            textAlign: 'left'
        },
        '& tbody tr:hover': {
            backgroundColor: '#fffbf2',
            cursor: 'pointer'
        }
    }
}))

const TablePaper = styled(Paper)(() => ({
    boxShadow: '0px 5px 5px -3px #d2d2d2,0px 8px 10px 1px #d2d2d2,0px 3px 14px 2px #d2d2d2'
}))

function TableServer(props) {
    const classes = useStyles()
    const navigate = useNavigate()
    const { user } = useAuth()
    const globalContext = useContext(GlobalContext)
    const loadDataRef = useRef(0)

    const {
        headCells,
        service,
        Form,
        title,
        drawer,
        haveAccessTo,
        currentPage,
        hideToolbar,
        showSiteButton,
        setNeedLoad,
        needLoad,
        filterFromList,
        activeCenterFromList,
        activeCenterComFromList
    } = props

    const [records, setRecords] = useState([])
    const [loading, setLoading] = useState(false)
    const [page, setPage] = useState(0)
    const [rowsPerPage, setRowsPerPage] = useState(10)
    const [total, setTotal] = useState(0)
    const [search, setSearch] = useState('')
    const [filters, setFilters] = useState({})
    const [openPopup, setOpenPopup] = useState(false)
    const [openDrawer, setOpenDrawer] = useState(false)
    const [recordForEdit, setRecordForEdit] = useState(null)
    const [confirmDialog, setConfirmDialog] = useState({ isOpen: false })

    const loadData = async () => {
        setLoading(true)
        const requestId = ++loadDataRef.current // identifiant unique pour cette requête

        try {
            let params = {}

            if (filterFromList) {
                params = { ...filterFromList }
            } else {
                params = { ...filters }
            }

            if (activeCenterFromList !== 'tct' || globalContext?.value?.activeCenter !== 'tct') {
                params = {
                    ...params,
                    center: activeCenterFromList || globalContext?.value?.activeCenter
                }
            }

            if (activeCenterComFromList !== '' || globalContext?.value?.activeCenterCom !== '') {
                params = {
                    ...params,
                    com: activeCenterComFromList || globalContext?.value?.activeCenterCom
                }
            }

            const res = await service.getAll({
                ...params,
                page: page + 1,
                limit: rowsPerPage,
                search,
                planting_year: globalContext?.value?.year
            })

            if (requestId !== loadDataRef.current) {
                console.log('🚫 Réponse obsolète ignorée', requestId, 'vs', loadDataRef.current)
                return
            }
            console.log('✅ Réponse appliquée', requestId, res.data?.length, res.payload?.total)
            // si une requête plus récente a été lancée entre temps, on ignore cette réponse
            if (requestId !== loadDataRef.current) return

            if (res) {
                setRecords(res.data || [])
                setTotal(res.payload?.total || 0)
            }
        } catch (error) {
            console.error(error)
        }

        if (requestId === loadDataRef.current) setLoading(false)
    }

    useEffect(() => {
        loadData()
    }, [
        page,
        rowsPerPage,
        search,
        filters,
        filterFromList,
        activeCenterFromList,
        activeCenterComFromList,
        globalContext?.value?.year,
        needLoad
    ])

    const handleSearch = (e) => {
        setSearch(e.target.value)
        setPage(0)
    }

    const addOrEdit = async (data, resetForm) => {
        try {
            if (data.id === 0) await service.create(data)
            else await service.update(data.id, data)

            new Notification(data.id === 0 ? service.messages.create_success : service.messages.update_success, 1)
            resetForm()
            setOpenPopup(false)
            setOpenDrawer(false)
            setRecordForEdit(null)
            loadData()
        } catch (error) {
            console.error(error)
        }
    }

    const onDelete = async (id) => {
        console.log('delete : ', id)
        setConfirmDialog({
            ...confirmDialog,
            isOpen: false
        })
        const result = await service.delete(id)

        if (result === 'undefined') new Notification(service.messages.delete_error, 0)
        else new Notification(service.messages.delete_success, 1)
        loadData()
    }

    const onRestore = async (item) => {
        try {
            await service.restore(item)
            new Notification(service.messages.restore_success, 1)
            loadData()
        } catch (error) {
            console.error(error)
        }
    }

    const formatData = (data, dataType, headCell, format) => {
        console.log(data)
        if (data == null) return '-'
        switch (dataType) {
            case 'date':
                return format ? moment(data).format(format) : moment(data).format('DD/MM/YYYY')
            case 'species':
                return <i>{data}</i>
            case 'year':
                return new Date(data).getFullYear()
            case 'photo':
                return <Controls.CustomImageAvatar title="Photo" place="left" size={80} src={`${data}`} />
            case 'Integer':
                return isNumber(data) ? numStr(data ? parseInt(data) : '', '.') : ''
            case 'Integer_ha':
                return Number(data).toFixed(2).toLocaleString('fr-FR', { maximumFractionDigits: 2 })
            default:
                if (typeof data === 'object' && headCell?.cell) {
                    const column = headCell.cell
                    return typeof data[column] === 'object' && headCell.data
                        ? data[column][headCell.data]
                        : data[column] ?? '-'
                }
                return data
        }
    }

    const exportToExcel = async () => {
        setLoading(true)
        try {
            const res = await service.getAll({
                page: 1,
                limit: 100000,
                search,
                ...filters,
                planting_year: globalContext?.value?.year
            })
            if (!res?.data?.length) {
                new Notification('Aucune donnée à exporter', 2)
                setLoading(false)
                return
            }
            const excelData = res.data.map((item) => ({
                Code: item.code,
                Village: item.village,
                FKT: item.fkt,
                Commune: item.commune,
                District: item.district,
                Région: item.region,
                Surface: item.surface,
                Technicien: item.technicien
            }))
            const worksheet = XLSX.utils.json_to_sheet(excelData)
            const workbook = XLSX.utils.book_new()
            XLSX.utils.book_append_sheet(workbook, worksheet, 'Lands')
            const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' })
            const file = new Blob([excelBuffer], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            })
            saveAs(file, `lands_${moment().format('YYYY_MM_DD_HH_mm')}.xlsx`)
        } catch (error) {
            console.error(error)
            new Notification('Erreur lors de l’export Excel', 0)
        }
        setLoading(false)
    }

    const toggleDrawer = (drawerOpen, item = null) => {
        setOpenDrawer(drawerOpen)
        setRecordForEdit(drawerOpen && item ? item : null)
    }

    return (
        <>
            <Paper className={classes.pageContent} elevation={0}>
                {!hideToolbar && (
                    <div className="flex gap-5">
                        <div className="items-center w-[50%]">
                            <Toolbar
                                handleSearch={handleSearch}
                                filter={filters}
                                setFilter={setFilters}
                                hideFilterButton
                                showSiteButton={showSiteButton}
                                setNeedLoad={setNeedLoad}
                            />
                        </div>
                        <div
                            className="flex w-[100px] h-[60px] items-center justify-end hover:cursor-pointer"
                            onClick={exportToExcel}
                        >
                            <img src={iconExcel} alt="iconExcel" />
                        </div>
                    </div>
                )}

                <TablePaper sx={{ p: 2, mt: 2 }}>
                    <TableContainer>
                        <Table className={classes.table}>
                            <TableHead>
                                <TableRow>
                                    {headCells.map((cell) => (
                                        <TableCell key={cell.id} align="center">
                                            {cell.label}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {records.length > 0 ? (
                                    records.map((item, index) => (
                                        <TableRow key={item.identifyer ?? item.id ?? index}>
                                            {headCells.map((cell, idx) => {
                                                const value = item[cell.id] ?? '-'
                                                if (cell.type !== 'actions') {
                                                    return (
                                                        <TableCell
                                                            key={idx}
                                                            className={classes.cell}
                                                            onClick={() => {
                                                                if (!cell.link) {
                                                                    const haveView = headCells.filter(
                                                                        (cells) => cells.showView && !cells.link
                                                                    )
                                                                    if (haveView && !cell.condition) {
                                                                        console.log('have view : ', haveView)
                                                                        console.log('item : ', item)
                                                                        toggleDrawer(true, item)
                                                                    }
                                                                }
                                                            }}
                                                        >
                                                            {cell.condition === false ? (
                                                                <Chip
                                                                    label={formatData(value, cell.type, cell)}
                                                                    sx={{
                                                                        '& .MuiChip-label': { fontSize: '0.8rem' },
                                                                        '&:hover': {
                                                                            cursor: 'pointer',
                                                                            backgroundColor: '#c8c8c8'
                                                                        }
                                                                    }}
                                                                />
                                                            ) : cell.link ? (
                                                                <Chip
                                                                    onClick={() =>
                                                                        cell.showView &&
                                                                        navigate(
                                                                            `${cell.showView.link}/${
                                                                                item[cell.showView.param]
                                                                            }`,
                                                                            {
                                                                                state: {
                                                                                    code: item[cell.showView.param]
                                                                                }
                                                                            }
                                                                        )
                                                                    }
                                                                    label={formatData(value, cell.type, cell)}
                                                                />
                                                            ) : cell.render ? (
                                                                cell.render(item)
                                                            ) : cell.showGrid ? (
                                                                <>test</>
                                                            ) : cell.type === 'hover' ? (
                                                                <Tooltip
                                                                    key={cell.id}
                                                                    title={cell.hover(item)}
                                                                    arrow
                                                                    placement="right"
                                                                    slotProps={{
                                                                        tooltip: {
                                                                            sx: {
                                                                                color: '#514E6A',
                                                                                backgroundColor: '#fff',
                                                                                border: '1px solid #514E6A'
                                                                            }
                                                                        }
                                                                    }}
                                                                >
                                                                    <i style={{ cursor: 'pointer', color: '#514E6A' }}>
                                                                        <b>voir</b>
                                                                    </i>
                                                                </Tooltip>
                                                            ) : cell.type === 'concatenated' ? (
                                                                <span>
                                                                    {item.firstname} {item.lastname}
                                                                </span>
                                                            ) : cell.type === 'table' ? (
                                                                Array.isArray(item[cell.id]) &&
                                                                item[cell.id].length > 0 ? (
                                                                    item[cell.id].join(', ')
                                                                ) : (
                                                                    '-'
                                                                )
                                                            ) : (
                                                                formatData(value, cell.type, cell, cell.format)
                                                            )}
                                                        </TableCell>
                                                    )
                                                } else {
                                                    return (
                                                        <TableCell key={idx} className={classes.cell}>
                                                            {cell.showEdit && (
                                                                <Controls.ActionButton
                                                                    tooltip="Modifier"
                                                                    disabled={!haveAccessTo(currentPage, 'edit')}
                                                                    onClick={() =>
                                                                        drawer
                                                                            ? toggleDrawer(true, item)
                                                                            : (setRecordForEdit(item),
                                                                              setOpenPopup(true))
                                                                    }
                                                                >
                                                                    <EditOutlinedIcon fontSize="small" />
                                                                </Controls.ActionButton>
                                                            )}
                                                            {cell.showRemove && (
                                                                <Controls.ActionButton
                                                                    disabled={!haveAccessTo(currentPage, 'delete')}
                                                                    tooltip="Supprimer"
                                                                    color="primary"
                                                                    onClick={() => {
                                                                        setConfirmDialog({
                                                                            isOpen: true,
                                                                            successMessage: 'Suppression en cours ...',
                                                                            title: 'Vous voulez vraiment le supprimer?',
                                                                            subTitle:
                                                                                'Cette opération est irréversible.',
                                                                            onConfirm: () => {
                                                                                console.log(cell)
                                                                                if (cell.identifyer) {
                                                                                    let params = new URLSearchParams({
                                                                                        id: item?.identifyer,
                                                                                        userStory: [
                                                                                            user.firstname,
                                                                                            user.lastname
                                                                                        ].join(' ')
                                                                                    }).toString()
                                                                                    onDelete(params)
                                                                                } else {
                                                                                    if (cell.recommandation) {
                                                                                        console.log(cell.recommandation)
                                                                                        console.log(item)
                                                                                        const data = {}
                                                                                        cell.recommandation.map(
                                                                                            (recommandation) => {
                                                                                                data[recommandation] =
                                                                                                    item[recommandation]
                                                                                                return data
                                                                                            }
                                                                                        )
                                                                                        console.log(data)
                                                                                        let params =
                                                                                            new URLSearchParams({
                                                                                                ...data,
                                                                                                userStory: [
                                                                                                    user.firstname,
                                                                                                    user.lastname
                                                                                                ].join(' ')
                                                                                            }).toString()
                                                                                        onDelete(params)
                                                                                    } else {
                                                                                        let params =
                                                                                            new URLSearchParams({
                                                                                                id: item.id,
                                                                                                userStory: [
                                                                                                    user.firstname,
                                                                                                    user.lastname
                                                                                                ].join(' ')
                                                                                            }).toString()
                                                                                        onDelete(params)
                                                                                    }
                                                                                }
                                                                            }
                                                                        })
                                                                    }}
                                                                >
                                                                    <CloseIcon fontSize="small" />
                                                                </Controls.ActionButton>
                                                            )}
                                                            {cell.showRestore && (
                                                                <Controls.ActionButton
                                                                    tooltip="Restaurer"
                                                                    onClick={() => onRestore(item)}
                                                                >
                                                                    <FaTrashRestore />
                                                                </Controls.ActionButton>
                                                            )}
                                                            {cell.actionButton && (
                                                                <Chip
                                                                    onClick={() => cell.actions(item)}
                                                                    label={cell.content ? cell.content : value}
                                                                    sx={{ borderRadius: 0 }}
                                                                />
                                                            )}
                                                            {cell.customButtons?.map((Btn, i) => (
                                                                <Btn key={i} id={item.id} {...item} />
                                                            ))}
                                                        </TableCell>
                                                    )
                                                }
                                            })}
                                        </TableRow>
                                    ))
                                ) : (
                                    <>
                                        <td colSpan={40} style={{ textAlign: 'center' }}>
                                            Aucun résultat.
                                        </td>
                                    </>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <TablePagination
                        component="div"
                        count={total}
                        page={page}
                        onPageChange={(_, newPage) => setPage(newPage)}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={(e) => {
                            setRowsPerPage(parseInt(e.target.value, 10))
                            setPage(0)
                        }}
                        rowsPerPageOptions={[5, 10, 25, 50]}
                    />
                </TablePaper>
            </Paper>

            {!drawer && (
                <Popup title={title} openPopup={openPopup} setOpenPopup={setOpenPopup}>
                    <Form recordForEdit={recordForEdit} addOrEdit={addOrEdit} />
                </Popup>
            )}
            {drawer ? (
                <Drawer title={title} openDrawer={openDrawer} toggleDrawer={toggleDrawer}>
                    <Form recordForEdit={recordForEdit} addOrEdit={addOrEdit} />
                </Drawer>
            ) : (
                ''
            )}
            <ConfirmDialog confirmDialog={confirmDialog} setConfirmDialog={setConfirmDialog} />
            <Loader isLoading={loading} />
        </>
    )
}

export default TableServer
